import { ReactNode } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import View from '../atoms/view';
import { Card } from '../atoms/card';
import { Divider } from '../atoms/divider';
import { IconBadge } from '../atoms/iconBadge';
import { H3, H4, P } from '../atoms/text';
import { Colors, Spacing } from '../../constants/theme';
import { H1, Label, StatusValue, SubTitle, TextRegular } from '../atoms/text/text';

export type StatusSummaryItem = {
  id?: string;
  value: string;
  date?: string | null;
};

type StatusSummaryCardProps = {
  title: string;
  icon: ReactNode;
  highlightLabel?: string;
  highlightValue?: string | number | null;
  highlightSublabel?: string;
  highlightDate?: string | null;
  listLabel?: string;
  items?: StatusSummaryItem[];
  footerLabel: string;
  onFooterClick?: () => void;
  emptyMessage?: string;
};

export const StatusSummaryCard = ({
  title,
  icon,
  highlightLabel,
  highlightValue,
  highlightSublabel,
  highlightDate,
  listLabel,
  items = [],
  footerLabel,
  onFooterClick,
  emptyMessage = 'Nothing to show',
}: StatusSummaryCardProps) => {
  const hasHighlight =
    highlightValue !== undefined && highlightValue !== null && highlightValue !== '';

  return (
    <Card>
      <View extend={styles.header}>
        <IconBadge>{icon}</IconBadge>
        <SubTitle noPadding>
          {title}
        </SubTitle>
      </View>

      {hasHighlight ? (
        <>
          <View extend={styles.highlight}>
            {highlightLabel ? (
              <Label noPadding >
                {highlightLabel}
              </Label>
            ) : null}
            <StatusValue text={highlightValue.toString()} />

            {highlightSublabel ? (
              <Label noPadding>
                {highlightSublabel}
              </Label>
            ) : null}
            {highlightDate ? (
              <Label noPadding>
                {highlightDate}
              </Label>
            ) : null}
          </View>

          {items.length > 0 ? (
            <>
              <Divider />
              <View extend={styles.listSection}>
                {listLabel ? (
                  <Label noPadding>
                    {listLabel}
                  </Label>
                ) : null}
                {items.map((item, index) => (
                  <View key={item.id ?? `${item.value}-${index}`} extend={styles.listEntry}>
                    {index > 0 ? <Divider /> : null}
                    <View extend={styles.listItem}>
                      <H3 noPadding>
                        {item.value}
                      </H3>
                      {item.date ? (
                        <Label noPadding>
                          {item.date}
                        </Label>
                      ) : null}
                    </View>
                  </View>
                ))}
              </View>
            </>
          ) : null}
        </>
      ) : (
        <H3 noPadding extend={{ color: Colors.light.placeholder }}>
          {emptyMessage}
        </H3>
      )}

      {hasHighlight ? (
        <View
          as="button"
          onClick={onFooterClick}
          disabled={!onFooterClick}
          extend={styles.footerLink}
        >
          <P noPadding extend={styles.footerText}>
            {footerLabel}
          </P>
          <KeyboardArrowRightIcon sx={{ fontSize: 18, color: Colors.light.green }} />
        </View>
      ) : null}
    </Card>
  );
};

const styles = {
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${6 * Spacing.gap}px`,
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    color: Colors.light.text,
    margin: 0,
  },
  highlight: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${4 * Spacing.gap}px`,
  },
  sectionLabel: {
    fontSize: '13px',
    color: Colors.light.placeholder,
    margin: 0,
  },
  highlightValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: Colors.light.text,
    margin: 0,
    lineHeight: 1.2,
  },
  metaText: {
    fontSize: '13px',
    color: Colors.light.placeholder,
    margin: 0,
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${8 * Spacing.gap}px`,
    flex: 1,
  },
  listEntry: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${6 * Spacing.gap}px`,
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: `${8 * Spacing.gap}px`,
  },
  listValue: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.light.text,
    margin: 0,
  },
  footerLink: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${2 * Spacing.gap}px`,
    marginTop: 'auto',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    font: 'inherit',
    color: Colors.light.green,
    '&:disabled': {
      cursor: 'default',
      opacity: 1,
    },
  },
  footerText: {
    color: Colors.light.green,
  },
};
